import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import { useUserStore } from '@/stores/user'
import { useAdminStore } from '@/stores/admin'
import NProgress from 'nprogress'
import 'nprogress/nprogress.css'

NProgress.configure({ showSpinner: false })

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/Login/index.vue'),
    meta: { public: true, title: '登录' },
  },
  {
    path: '/register',
    name: 'Register',
    component: () => import('@/views/Register/index.vue'),
    meta: { public: true, title: '用户注册' },
  },
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    component: () => import('@/views/ForgotPassword/index.vue'),
    meta: { public: true, title: '找回密码' },
  },
  {
    path: '/bind',
    name: 'Bind',
    component: () => import('@/views/Bind/index.vue'),
    meta: { public: true, title: '绑定手机号' },
  },
  {
    path: '/',
    redirect: '/login',
  },
  {
    path: '/app',
    component: () => import('@/components/Layout/index.vue'),
    redirect: '/app/search',
    children: [
      {
        path: 'search',
        name: 'Search',
        component: () => import('@/views/Search/index.vue'),
        meta: { title: '房产搜索' },
      },
      {
        path: 'ranking',
        name: 'Ranking',
        component: () => import('@/views/Ranking/index.vue'),
        meta: { title: '价格排行' },
      },
      {
        path: 'attention',
        name: 'Attention',
        component: () => import('@/views/Attention/index.vue'),
        meta: { title: '我的关注' },
      },
      {
        path: 'crm',
        name: 'CRM',
        component: () => import('@/views/CRM/index.vue'),
        meta: { title: '客户管理' },
      },
    ],
  },
  {
    path: '/admin/login',
    name: 'AdminLogin',
    component: () => import('@/views/Admin/Login/index.vue'),
    meta: { public: true, title: '管理端登录' },
  },
  {
    path: '/admin',
    component: () => import('@/views/Admin/Layout/index.vue'),
    redirect: '/admin/users',
    children: [
      {
        path: 'users',
        name: 'AdminUsers',
        component: () => import('@/views/Admin/Users/index.vue'),
        meta: { title: '用户管理' },
      },
      {
        path: 'crm',
        name: 'AdminCrm',
        component: () => import('@/views/Admin/CRM/index.vue'),
        meta: { title: 'CRM管理' },
      }
    ]
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/views/NotFound/index.vue'),
    meta: { public: true, title: '页面不存在' },
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior() {
    return { top: 0 }
  },
})

router.beforeEach(async (to, _from, next) => {
  NProgress.start()
  
  if (to.meta.title) {
    document.title = `${to.meta.title} - 华屋数据`
  }
  
  // 如果是公开页面，直接放行
  if (to.meta.public) {
    return next()
  }

  // 管理端路由鉴权
  if (to.path.startsWith('/admin')) {
    const adminStore = useAdminStore()
    if (!adminStore.token) {
      return next('/admin/login')
    }
    return next()
  }

  // 客户端路由鉴权
  const userStore = useUserStore()
  if (!userStore.token) {
    return next('/login')
  }

  // 如果有 token，但内存里没有 userInfo（例如刷新了页面）
  // 则静默调用 fetchUserInfo 拉取用户信息，顺便验证 token 有效性
  if (!userStore.userInfo) {
    try {
      await userStore.fetchUserInfo()
      // 拉取成功，说明 token 仍然有效
      return next()
    } catch (error) {
      // 拉取失败（401），说明 token 已失效，被 axios 拦截器处理后会清空并跳转登录
      // 这里只需要终止当前的路由导航即可
      return next(false)
    }
  }

  // 既有 token 又有 userInfo，直接放行
  next()
})

router.afterEach(() => {
  NProgress.done()
})

export default router
